import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { trackFileUpload, trackDemoLoad, trackResultsViewed } from './analytics';
import Header from './components/Header';
import AuthForm from './components/AuthForm';
import UploadSection from './components/UploadSection';
import FilterBar from './components/FilterBar';
import SortControls from './components/SortControls';
import StatisticsCard from './components/StatisticsCard';
import ResultsSection from './components/ResultsSection';
import AIAssistant from './components/AIAssistant';
import SNPDetailModal from './components/SNPDetailModal';
import { loadClinVarData, clearCache } from './utils/clinvarDatabase-API';
import { parseGeneticFile, analyzeSNPs } from './utils/geneticParser-API';
import {
  sortByClinicalImportance,
  sortByBeneficialFirst,
  groupByRiskCategory,
  getRiskCategoryCounts,
  hasCarriedAssociations,
} from './utils/riskInterpreter';
import {
  snpMatchesDiseaseFilter,
  getDiseaseCategoryCounts,
} from './utils/traitCategories';
import { Loader2, Lock } from 'lucide-react';
import SNPSearchBar from './components/SNPSearchBar';

// ---------------------------------------------------------------------------
// Ancestry helpers
// ---------------------------------------------------------------------------

const snpMatchesAncestry = (snp, ancestryFilter) => {
  if (!ancestryFilter || !snp.gwas?.topAssociations?.length) return true;
  return snp.gwas.topAssociations.some(a =>
    a.ancestries?.some(anc => anc && anc.includes(ancestryFilter))
  );
};

const collectAvailableAncestries = (snps) => {
  const seen = new Set();
  snps.forEach(snp => {
    snp.gwas?.topAssociations?.forEach(a => {
      a.ancestries?.forEach(anc => { if (anc) seen.add(anc); });
    });
  });
  return [...seen];
};

// ---------------------------------------------------------------------------
// Main app content (extracted so Routes can wrap it cleanly)
// ---------------------------------------------------------------------------

const MainApp = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedSnp, setSelectedSnp] = useState(null);
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [loadingDatabase, setLoadingDatabase] = useState(true);
  const [activeFilters, setActiveFilters] = useState(['all']);
  const [currentSort, setCurrentSort] = useState('clinical-importance');
  const [filteredSnps, setFilteredSnps] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [resultsKey, setResultsKey] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
  const [shouldSave, setShouldSave] = useState(false);
  const [showCarriedOnly, setShowCarriedOnly] = useState(false);
  const [ancestryFilter, setAncestryFilter] = useState(null);
  const [diseaseFilter, setDiseaseFilter] = useState([]);
  const [diseaseCategoryCounts, setDiseaseCategoryCounts] = useState({});
  const [availableAncestries, setAvailableAncestries] = useState([]);
  const [analysisProgress, setAnalysisProgress] = useState({
    stage: '',
    clinvarProgress: 0,
    clinvarCount: 0,
    gwasProgress: 0,
    gwasCount: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  useEffect(() => {
    if (results && results.snps && results.snps.length > 0) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [results?.analyzedSnps]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('shouldSaveFiles');
    if (saved !== null) setShouldSave(saved === 'true');
  }, []);

  useEffect(() => {
    const checkBackend = async () => {
      const available = await loadClinVarData();
      setBackendAvailable(available);
      setLoadingDatabase(false);
    };
    checkBackend();
  }, []);

  useEffect(() => {
    if (results && results.snps) {
      let base = results.snps;

      if (showCarriedOnly) {
        base = base.filter(snp => hasCarriedAssociations(snp));
      }

      if (ancestryFilter) {
        base = base.filter(snp => snpMatchesAncestry(snp, ancestryFilter));
      }

      if (diseaseFilter.length > 0) {
        base = base.filter(snp => snpMatchesDiseaseFilter(snp, diseaseFilter));
      }

      setDiseaseCategoryCounts(getDiseaseCategoryCounts(base));

      let filtered = base;
      if (!activeFilters.includes('all')) {
        const groups = groupByRiskCategory(base);
        filtered = [];
        activeFilters.forEach(filter => {
          if (groups[filter]) filtered.push(...groups[filter]);
        });
      }

      filtered = applySortToSnps(filtered, currentSort);

      setFilteredSnps(filtered);
      setCategoryCounts(getRiskCategoryCounts(base));
      setAvailableAncestries(collectAvailableAncestries(results.snps));
    }
  }, [results, activeFilters, currentSort, showCarriedOnly, ancestryFilter, diseaseFilter]);

  const applySortToSnps = (snps, sort) => {
    const arr = [...snps];
    switch (sort) {
      case 'clinical-importance':
        return sortByClinicalImportance(arr);
      case 'clinical-importance-reverse':
        return sortByBeneficialFirst(arr);
      case 'gene':
        return arr.sort((a, b) => (a.gene || '').localeCompare(b.gene || ''));
      case 'chromosome':
        return arr.sort((a, b) => {
          const chrA = parseInt(a.chromosome) || 999;
          const chrB = parseInt(b.chromosome) || 999;
          if (chrA !== chrB) return chrA - chrB;
          return parseInt(a.position) - parseInt(b.position);
        });
      case 'gwas-risk':
        return arr.sort((a, b) => {
          const aOR = a.gwas?.topAssociations?.[0]?.userEffectiveOdds || 1;
          const bOR = b.gwas?.topAssociations?.[0]?.userEffectiveOdds || 1;
          return Math.abs(bOR - 1) - Math.abs(aOR - 1);
        });
      default:
        return arr;
    }
  };

  const handleFileUpload = async (event) => {
    if (!currentUser) {
      alert('Please sign in to upload files');
      return;
    }

    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    clearCache(newSessionId);

    setFile(uploadedFile);
    setAnalyzing(true);
    setResults(null);
    setIsDemoMode(false);
    setActiveFilters(['all']);
    setCurrentSort('clinical-importance');
    setShowCarriedOnly(false);
    setAncestryFilter(null);
    setDiseaseFilter([]);
    setAvailableAncestries([]);
    setAnalysisProgress({ stage: 'parsing', clinvarProgress: 0, clinvarCount: 0, gwasProgress: 0, gwasCount: 0 });

    let uploadPromise = null;

    try {
      const content = await uploadedFile.text();

      if (shouldSave) {
        setAnalysisProgress(prev => ({ ...prev, stage: 'uploading' }));
        const timestamp = Date.now();
        const sanitizedFileName = uploadedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');

        uploadPromise = (async () => {
          try {
            const pako = await import('pako');
            const uint8Array = new TextEncoder().encode(content);
            const compressed = pako.gzip(uint8Array, { level: 6 });
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('./firebase');
            const rawFilePath = `users/${currentUser.uid}/genetic-files/${timestamp}_${sanitizedFileName}.gz`;
            const rawFileRef = ref(storage, rawFilePath);
            await uploadBytes(rawFileRef, compressed, {
              contentType: 'application/gzip',
              customMetadata: { originalName: uploadedFile.name, originalSize: uploadedFile.size.toString() }
            });
            const rawFileURL = await getDownloadURL(rawFileRef);
            return { rawFilePath, rawFileURL, compressedSize: compressed.length };
          } catch (err) {
            console.error('⚠️ Background upload failed:', err);
            return null;
          }
        })();

        setAnalysisProgress(prev => ({ ...prev, stage: 'parsing' }));
      }

      const snps = parseGeneticFile(content);
      console.log(`📋 Parsed ${snps.length.toLocaleString()} SNPs from file`);
      setAnalysisProgress(prev => ({ ...prev, stage: 'clinvar' }));

      const analyzed = await analyzeSNPs(snps, {
        sessionId: newSessionId,
        onClinVarProgress: (progress, count) => {
          setAnalysisProgress(prev => ({ ...prev, clinvarProgress: progress, clinvarCount: count }));
        },
        onGWASProgress: (progress, count) => {
          setAnalysisProgress(prev => ({ ...prev, stage: 'gwas', gwasProgress: progress, gwasCount: count }));
        },
        // No onPartialResults — we keep the loading screen up until GWAS finishes
        includeGWAS: true,
      });

      const finalResults = {
        totalSnps: snps.length,
        analyzedSnps: analyzed.length,
        snps: sortByClinicalImportance([...analyzed]),
        isPartial: false
      };
      setResults(finalResults);
      setAnalyzing(false);
      setAnalysisProgress({
        stage: 'complete',
        clinvarProgress: 100,
        clinvarCount: analyzed.length,
        gwasProgress: 100,
        gwasCount: 0
      });
      setResultsKey(k => k + 1);
      trackFileUpload({
        fileType: uploadedFile.name.toLowerCase().includes('ancestry') ? 'ancestry' : '23andme',
        totalSnps: finalResults.totalSnps,
        analyzedSnps: finalResults.analyzedSnps,
        savedToCloud: shouldSave,
      });
      trackResultsViewed({ totalSnps: finalResults.analyzedSnps, plan: 'free' });

      if (shouldSave && uploadPromise) {
        try {
          setAnalysisProgress(prev => ({ ...prev, stage: 'saving' }));
          const uploadResult = await uploadPromise;
          if (!uploadResult) throw new Error('File upload failed');

          const analysisData = {
            snps: finalResults.snps.map(snp => ({
              rsid: snp.rsid || null,
              chromosome: snp.chromosome || null,
              position: snp.position || null,
              genotype: snp.genotype || null,
              gene: snp.gene || null,
              clinicalSignificance: snp.clinicalSignificance || null,
              repute: snp.repute || null,
              magnitude: snp.magnitude || null,
              summary: snp.summary || null,
              conditions: snp.conditions || null,
              riskCategory: snp.riskCategory || null,
              gwas: snp.gwas || null
            }))
          };

          const analysisBlob = new Blob([JSON.stringify(analysisData)], { type: 'application/json' });
          const timestamp = Date.now();
          const sanitizedFileName = uploadedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const analysisPath = `users/${currentUser.uid}/genetic-files/${timestamp}_${sanitizedFileName}_analysis.json`;
          const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const { storage } = await import('./firebase');
          const analysisRef = ref(storage, analysisPath);
          await uploadBytes(analysisRef, analysisBlob);
          const analysisURL = await getDownloadURL(analysisRef);

          const { collection, addDoc } = await import('firebase/firestore');
          const { db } = await import('./firebase');
          const firestoreDoc = {
            userId: currentUser.uid,
            fileName: uploadedFile.name,
            uploadedAt: new Date(),
            lastAnalyzedAt: new Date(),
            originalSize: uploadedFile.size,
            compressedSize: uploadResult.compressedSize,
            compressionRatio: parseFloat(((1 - uploadResult.compressedSize / uploadedFile.size) * 100).toFixed(1)),
            rawFilePath: uploadResult.rawFilePath,
            rawFileURL: uploadResult.rawFileURL,
            analysisPath,
            analysisURL,
            isCompressed: true,
            stats: { totalSnps: finalResults.totalSnps || 0, analyzedSnps: finalResults.analyzedSnps || 0 }
          };
          const docRef = await addDoc(collection(db, 'geneticAnalyses'), firestoreDoc);
          setCurrentAnalysisId(docRef.id);
        } catch (storageError) {
          console.error('⚠️ Failed to save:', storageError);
          alert('Analysis complete, but cloud save failed. You can still use your results.');
        }
      }

    } catch (error) {
      console.error('❌ Error analyzing file:', error);
      setAnalyzing(false);
      alert('Error analyzing file. Please check the file format and try again.');
    }
  };

  const handleLoadAnalysis = async (analysis) => {
    try {
      setAnalyzing(true);
      setResults(null);

      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('./firebase');
      const analysisDoc = await getDoc(doc(db, 'geneticAnalyses', analysis.id));
      if (!analysisDoc.exists()) throw new Error('Analysis not found');

      const data = analysisDoc.data();
      if (!data.analysisURL) throw new Error('Analysis data URL not found');

      let analysisData;
      try {
        const response = await fetch(data.analysisURL, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        analysisData = await response.json();
      } catch {
        analysisData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', data.analysisURL);
          xhr.responseType = 'json';
          xhr.onload = () => xhr.status === 200 ? resolve(xhr.response) : reject(new Error(`XHR ${xhr.status}`));
          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.send();
        });
      }

      if (!analysisData?.snps) throw new Error('Invalid analysis data format');

      setResults({
        totalSnps: data.stats.totalSnps,
        analyzedSnps: data.stats.analyzedSnps,
        snps: sortByClinicalImportance(analysisData.snps || []),
        isPartial: false
      });
      setCurrentAnalysisId(analysis.id);
      setShowCarriedOnly(false);
      setAncestryFilter(null);
      setDiseaseFilter([]);
      setAvailableAncestries([]);
      setAnalyzing(false);
      setResultsKey(k => k + 1);
    } catch (error) {
      console.error('❌ Error loading analysis:', error);
      setAnalyzing(false);
      let errorMessage = `Error loading analysis: ${error.message}`;
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        errorMessage += '\n\nThis is likely a Firebase Storage CORS issue.';
      }
      alert(errorMessage);
    }
  };

  const handleLoadDemoData = async (demoType) => {
    try {
      setAnalyzing(true);
      setResults(null);
      setIsDemoMode(true);
      setShowCarriedOnly(false);
      setAncestryFilter(null);
      setDiseaseFilter([]);
      setAvailableAncestries([]);

      // Try loading from Firebase Storage cache (via Firestore URL index)
      const cacheDocId = `demo_${demoType}`;
      let analyzed = null;
      let totalSnps = 0;
      let cacheHit = false;

      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');
        const cacheDoc = await getDoc(doc(db, 'demoCache', cacheDocId));
        if (cacheDoc.exists() && cacheDoc.data().downloadURL) {
          cacheHit = true;
          const { downloadURL } = cacheDoc.data();
          totalSnps = cacheDoc.data().totalSnps || 0;

          // Fetch compressed file from Storage
          const response = await fetch(downloadURL);
          if (!response.ok) throw new Error(`Storage fetch failed: ${response.status}`);
          const arrayBuffer = await response.arrayBuffer();

          // Decompress
          const pakoModule = await import('pako');
          const pako = pakoModule.default || pakoModule;
          const decompressed = pako.ungzip(new Uint8Array(arrayBuffer), { to: 'string' });
          const parsed = JSON.parse(decompressed);
          analyzed = parsed.snps;
          totalSnps = parsed.totalSnps || totalSnps;
          console.log(`⚡ Demo loaded from Storage cache (${analyzed.length} variants)`);
        }
      } catch (cacheErr) {
        console.warn('Demo cache miss, running full analysis:', cacheErr.message);
        analyzed = null; // ensure we fall through to full analysis
      }

      if (!analyzed) {
        // Cache miss — run full analysis and store result
        const demoFiles = {
          '23andme': '/sample_genome_23andMe.txt',
          'ancestry': '/sample_genome_ancestry.txt'
        };
        const filePath = demoFiles[demoType];
        if (!filePath) throw new Error('Unknown demo type');

        const response = await fetch(filePath);
        if (!response.ok) throw new Error(`Failed to load demo file: ${response.statusText}`);
        const fileContent = await response.text();

        const newSessionId = `demo_${demoType}_${Date.now()}`;
        setSessionId(newSessionId);
        clearCache(newSessionId);
        setAnalysisProgress({ stage: 'parsing', clinvarProgress: 0, clinvarCount: 0, gwasProgress: 0, gwasCount: 0 });

        const snps = parseGeneticFile(fileContent);
        totalSnps = snps.length;
        setAnalysisProgress(prev => ({ ...prev, stage: 'clinvar' }));

        analyzed = await analyzeSNPs(snps, {
          sessionId: newSessionId,
          onClinVarProgress: (progress, count) => {
            setAnalysisProgress(prev => ({ ...prev, clinvarProgress: progress, clinvarCount: count }));
          },
          onGWASProgress: (progress, count) => {
            setAnalysisProgress(prev => ({ ...prev, stage: 'gwas', gwasProgress: progress, gwasCount: count }));
          },
          includeGWAS: true,
        });

        // Save to Firebase Storage (compressed) — fire and forget, never blocks results
        const writeDemoCache = async () => {
          try {
            const pakoModule = await import('pako');
            const pako = pakoModule.default || pakoModule;
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            const { doc: fsDoc, setDoc } = await import('firebase/firestore');
            const { storage, db } = await import('./firebase');

            if (!storage) throw new Error('Firebase Storage not initialized');

            console.log('📦 Compressing demo data...');
            const json = JSON.stringify({ snps: analyzed, totalSnps });
            const uint8Array = new TextEncoder().encode(json);
            const compressed = pako.gzip(uint8Array, { level: 6 });
            console.log(`📦 Compressed demo: ${(compressed.length / 1024).toFixed(0)}KB — uploading to Storage...`);

            const storagePath = `demo-cache/${cacheDocId}.json.gz`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, compressed, {
              contentType: 'application/gzip',
              customMetadata: { analyzedSnps: String(analyzed.length), totalSnps: String(totalSnps) }
            });
            const downloadURL = await getDownloadURL(storageRef);

            await setDoc(fsDoc(db, 'demoCache', cacheDocId), {
              downloadURL,
              storagePath,
              totalSnps,
              analyzedSnps: analyzed.length,
              cachedAt: new Date(),
            });

            console.log(`✅ Demo cached to Storage: ${storagePath}`);
          } catch (cacheWriteErr) {
            console.warn('Demo cache write failed (non-blocking):', cacheWriteErr.message);
          }
        };
        writeDemoCache(); // intentionally not awaited — never blocks results
      }

      const finalResults = {
        totalSnps,
        analyzedSnps: analyzed.length,
        snps: sortByClinicalImportance([...analyzed]),
        isPartial: false
      };

      setResults(finalResults);
      setAnalyzing(false);
      setAnalysisProgress({
        stage: 'complete',
        clinvarProgress: 100,
        clinvarCount: analyzed.length,
        gwasProgress: 100,
        gwasCount: 0
      });
      setResultsKey(k => k + 1);
      trackDemoLoad(demoType, cacheHit);
      trackResultsViewed({ totalSnps: finalResults.analyzedSnps, plan: 'free' });
    } catch (error) {
      console.error('❌ Error loading demo data:', error);
      setAnalyzing(false);
      alert(`Error loading demo file: ${error.message}`);
    }
  };

  const handleNewUpload = () => {
    if (sessionId) clearCache(sessionId);
    setResults(null);
    setFile(null);
    setIsDemoMode(false);
    setSessionId(null);
    setCurrentAnalysisId(null);
    setActiveFilters(['all']);
    setCurrentSort('clinical-importance');
    setShowCarriedOnly(false);
    setAncestryFilter(null);
    setDiseaseFilter([]);
    setAvailableAncestries([]);
    setFilteredSnps([]);
    setAnalysisProgress({ stage: '', clinvarProgress: 0, clinvarCount: 0, gwasProgress: 0, gwasCount: 0 });
  };

  if (loadingDatabase) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-700">Connecting to ClinVar database...</p>
          <p className="text-sm text-slate-500 mt-2">Checking backend availability</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" id="app-root">
      <Header onLoadFile={handleLoadAnalysis} />

      <div className="container mx-auto px-6 pb-12 max-w-7xl">
        {!backendAvailable && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 shadow-sm">
            <p className="text-amber-900 text-sm">
              <strong>⚠️ Backend Offline:</strong> Database features unavailable. Start the backend server or deploy to production.
            </p>
          </div>
        )}
        {/* Payment success toast */}


        <div className="grid lg:grid-cols-12 gap-6">
          {results && currentUser && (
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-6 space-y-3">
                <FilterBar
                  activeFilters={activeFilters}
                  onFilterChange={setActiveFilters}
                  categoryCounts={categoryCounts}
                  showCarriedOnly={showCarriedOnly}
                  onCarriedToggle={setShowCarriedOnly}
                  ancestryFilter={ancestryFilter}
                  onAncestryChange={setAncestryFilter}
                  availableAncestries={availableAncestries}
                  diseaseFilter={diseaseFilter}
                  onDiseaseFilterChange={setDiseaseFilter}
                  diseaseCategoryCounts={diseaseCategoryCounts}
                />
                <SortControls
                  currentSort={currentSort}
                  onSortChange={setCurrentSort}
                />
              </div>
            </div>
          )}

          <div className={results && currentUser ? "lg:col-span-6" : "lg:col-span-9"}>
            {!currentUser ? (
              <AuthForm />
            ) : !results ? (
              <UploadSection
                fileInputRef={fileInputRef}
                analyzing={analyzing}
                onFileUpload={handleFileUpload}
                progress={analysisProgress}
                shouldSave={shouldSave}
                onShouldSaveChange={setShouldSave}
                onDemoDataSelect={handleLoadDemoData}
              />
            ) : (
              <div className="space-y-6">
                <SNPSearchBar
                  allSnps={results.snps}
                  onSnpClick={setSelectedSnp}
                />
                <StatisticsCard
                  results={results}
                  filteredResults={filteredSnps}
                  analysisProgress={analysisProgress}
                />
                <ResultsSection
                  key={resultsKey}
                  results={{ ...results, snps: filteredSnps }}
                  onNewUpload={handleNewUpload}
                  onSnpClick={setSelectedSnp}
                  showingFiltered={!activeFilters.includes('all') || showCarriedOnly || !!ancestryFilter}
                  totalUnfilteredSnps={results.snps.length}
                  currentSort={currentSort}
                  isPartial={results.isPartial}
                  isDemoMode={isDemoMode}
                />
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <div className="relative" style={{ scrollMarginTop: '2rem' }}>
              {!currentUser ? (
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                  <div className="text-center">
                    <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">AI Assistant Locked</h3>
                    <p className="text-sm text-slate-500">
                      Sign in to access personalized genetic insights and AI-powered analysis
                    </p>
                  </div>
                </div>
              ) : (
                <AIAssistant results={results} />
              )}
            </div>
          </div>
        </div>

        {selectedSnp && currentUser && (
          <SNPDetailModal snp={selectedSnp} onClose={() => setSelectedSnp(null)} />
        )}

        <footer className="mt-16 pt-8 border-t border-slate-200">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-300 rounded-lg">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
              <p className="text-xs font-medium text-slate-700">
                <span className="font-semibold">Beta Version</span> · We'd love your feedback
              </p>
            </div>
            <p className="text-sm text-slate-600">
              <strong>Educational Use Only</strong> · This tool provides research insights, not medical advice.
            </p>
            <p className="text-xs text-slate-500">
              Consult with a genetic counselor or healthcare provider for personalized medical guidance.
            </p>
            <div className="flex items-center justify-center gap-3 text-xs text-slate-400">
              <span>ClinVar Database (NCBI)</span>
              <span>•</span>
              <span>GWAS Catalog (EMBL-EBI)</span>
              <span>•</span>
              <span>Powered by Claude AI</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const App = () => (
  <Routes>
    <Route path="/*" element={<MainApp />} />
  </Routes>
);

export default App;